import type { Options } from '@cfa/pathway-fe-shared/xp';

interface TDataShape {
  body?: unknown;
  headers?: unknown;
  path?: unknown;
  query?: unknown;
  url: string;
}

/**
 * Extracts the query ID from any generated TanStack Query key function.
 * Returns an object with the _id property for use in partial query matching.
 *
 * @example
 * queryClient.invalidateQueries({
 *   queryKey: [getBroadQueryKeyId(getPlanAndAssignmentsQueryKey)],
 *   refetchType: 'all',
 * });
 *
 * @remarks
 * The parameter type uses `(...args: never[])` instead of `(options: Options<T>)`
 * to work around TypeScript's contravariant parameter checking. Generated query key
 * functions are inconsistent — some have required params `(options: Options<T>)` and
 * others have optional params `(options?: Options<T>)`. A single concrete parameter
 * type cannot satisfy both due to contravariance. `...args: never[]` bypasses the
 * parameter check entirely; `T` is still inferred and constrained via the return type
 * `[Options<T> & { _id: string }]`, so invalid non-query-key functions will still
 * produce a type error at the call site.
 */
export const getBroadQueryKeyId = <T extends TDataShape>(
  queryKeyFunction: (...args: never[]) => [Options<T> & { _id: string }],
) => ({
  _id: (queryKeyFunction as unknown as (options: Options) => [{ _id: string }])(
    {},
  )[0]._id,
});
